import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { RUN_PERIOD_LABELS, RUN_STATUS_LABELS, RUN_STATUSES, TAX_NOTICE, formatCents, type PayrollOverview, type PayrollRunSummary } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { useSession } from '../../session';
import './payroll.css';

export const dayDe = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '–');
export const periodText = (r: Pick<PayrollRunSummary, 'period' | 'periodStart' | 'periodEnd'>) =>
  r.period === 'month' ? new Date(`${r.periodStart.slice(0, 7)}-15T12:00:00Z`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : r.period === 'day' ? dayDe(r.periodStart) : `${dayDe(r.periodStart)} – ${dayDe(r.periodEnd)}`;
export const statusBadge = (s: PayrollRunSummary['status']) => <span className={`badge ${s === 'paid' ? 'badge--won' : s === 'cancelled' ? 'badge--rejected' : ''}`}>{RUN_STATUS_LABELS[s]}</span>;

/** Übersicht der Löhne: Erinnerungen, geschätzte Lohnkosten und die Lohnläufe. Suche und Filter stehen in der Adresszeile. */
export function PayrollPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const status = params.get('stand') ?? '';
  const year = params.get('jahr') ?? '';
  const q = (params.get('q') ?? '').toLowerCase();

  const overview = useQuery({ queryKey: ['payroll-overview'], queryFn: async () => (await api<{ data: PayrollOverview }>('GET', '/payroll/overview')).data });
  const list = useQuery({ queryKey: ['payroll-runs', { status, year }], queryFn: async () => (await api<{ data: PayrollRunSummary[] }>('GET', `/payroll/runs${queryString({ status, year })}`)).data });

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    setParams(merged);
  };
  const thisYear = new Date().getFullYear();
  const fields: FilterField[] = [
    { key: 'stand', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, ...RUN_STATUSES.map((s) => ({ value: s, label: RUN_STATUS_LABELS[s] }))] },
    { key: 'jahr', label: 'Jahr', type: 'select', options: [{ value: '', label: 'Alle' }, ...[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => ({ value: String(y), label: String(y) }))] },
  ];
  const rows = (list.data ?? []).filter((r) => !q || r.number.toLowerCase().includes(q) || periodText(r).toLowerCase().includes(q) || (r.note ?? '').toLowerCase().includes(q));
  const o = overview.data;

  return (
    <>
      <div className="page-head">
        <h1>Löhne</h1>
        <span className="muted">{list.data ? `${list.data.length} Lohnläufe` : ''}</span>
        {can('payroll.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/loehne/neu">Neuer Lohnlauf</Link>}
        <Link className="btn btn--secondary btn--sm" to="/loehne/arbeitszeiten">Arbeitszeiten</Link>
      </div>
      <p className="payroll-notice">{TAX_NOTICE}</p>

      {o && (
        <div className="payroll-tiles">
          <div className="card payroll-tile"><span className="muted small">Lohnkosten je Monat (geschätzt)</span><strong>{formatCents(o.monthlyEmployerCostCents)}</strong><span className="muted small">{o.employeeCount} Mitarbeiter, mit Arbeitgeberanteilen</span></div>
          {o.withoutPay > 0 && <div className="card payroll-tile"><span className="muted small">Ohne Vergütung oder Stunden</span><strong>{o.withoutPay}</strong><span className="muted small">Ihre Kosten fehlen in der Schätzung</span></div>}
        </div>
      )}

      {o && o.reminders.length > 0 && (
        <div className="card">
          <h2>Erinnerungen</h2>
          <ul className="payroll-reminders">
            {o.reminders.map((r) => (
              <li key={r.key}>
                <span className={`payroll-dot${r.tone === 'alert' ? ' payroll-dot--alert' : ''}`} aria-hidden="true" />
                <Link to={r.to}>{r.text}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <FilterBar
        search={{ value: params.get('q') ?? '', placeholder: 'Nummer, Zeitraum oder Notiz', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ stand: status, jahr: year }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />

      {list.isError && <div className="alert alert--error" role="alert">{(list.error as Error).message}</div>}

      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Zeitraum</th>
              <th>Auszahlung am</th>
              <th>Stand</th>
              <th className="num">Mitarbeiter</th>
              <th className="num">Brutto</th>
              <th className="num">Auszahlung</th>
              <th className="num">Arbeitgeberkosten</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={8} className="muted">Wird geladen …</td></tr>}
            {list.data && rows.length === 0 && <tr><td colSpan={8} className="empty">{q || status || year ? 'Keine Lohnläufe zu dieser Suche.' : 'Noch kein Lohnlauf. Legen Sie den ersten über „Neuer Lohnlauf“ an.'}</td></tr>}
            {rows.map((r) => (
              <ExpandRow
                key={r.id}
                summary={<Summary title={<Link to={`/loehne/${r.id}`} className="strong-link">{r.number} · {periodText(r)}</Link>} line2={`${RUN_STATUS_LABELS[r.status]} · Auszahlung am ${dayDe(r.payDate)}`} line3={`${r.lineCount} Mitarbeiter · ${formatCents(r.payoutCents)}`} />}
              >
                <td data-label="Nummer" className="nowrap"><Link to={`/loehne/${r.id}`} className="strong-link">{r.number}</Link></td>
                <td data-label="Zeitraum">{periodText(r)} <span className="muted small">({RUN_PERIOD_LABELS[r.period]})</span></td>
                <td data-label="Auszahlung am">{dayDe(r.payDate)}</td>
                <td data-label="Stand">{statusBadge(r.status)}</td>
                <td data-label="Mitarbeiter" className="num">{r.lineCount}</td>
                <td data-label="Brutto" className="num">{formatCents(r.grossCents)}</td>
                <td data-label="Auszahlung" className="num">{formatCents(r.payoutCents)}</td>
                <td data-label="Arbeitgeberkosten" className="num">{formatCents(r.employerCostCents)}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
